import { MapContainer, Polyline, CircleMarker, Marker, Popup, Tooltip, useMap } from "react-leaflet";
import { useEffect, useMemo, useState, Fragment } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import { EVENT_TYPES, EVENT_ORDER } from "@/lib/historyEvents";
import { CAN_FIELDS, CAN_ORDER } from "@/lib/can";
import { noSignalSegments } from "@/lib/trips";
import { tripArrowIcon, tripDotIcon, tripFlagIcon } from "@/lib/mapIcons";
import { fmtTime as fmtTimeS } from "@/lib/userSettings";
import { ChevronDown, Flame } from "lucide-react";
import { TILES, TileManager, BaseTileLayer, ZoomInfo, MAP_MAX_ZOOM } from "@/components/MapTiles";
import { buildHeat, HEAT_MODES } from "@/lib/heatmap";

// Historique : Sombre / Satellite / OSM / Géorép / Ortho (fonds partagés avec Live Tracking).
const HISTORY_LAYERS = ["dark", "satellite", "hybride", "osm", "georep", "georep_sat"];
const HEAT_GRADIENT = { 0.2: "#2563EB", 0.4: "#06B6D4", 0.6: "#84CC16", 0.8: "#F59E0B", 1.0: "#EF4444" };

// Couche thermique — react-leaflet v5 : ajout/retrait manuel d'un L.heatLayer. Recalcul uniquement sur data/mode
// (jamais au zoom : le plugin réajuste seul le rendu en pixels).
function HeatLayer({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || !points.length) return;
    const layer = L.heatLayer(points, { radius: 24, blur: 18, max: 1, maxZoom: 16, minOpacity: 0.32, gradient: HEAT_GRADIENT });
    layer.addTo(map);
    return () => { map.removeLayer(layer); };
  }, [map, points]);
  return null;
}

const PALETTE = ["#17A2B8", "#F5821F", "#10B981", "#A855F7", "#EF4444", "#3B82F6", "#EAB308", "#EC4899", "#14B8A6", "#F97316"];
export const tripColor = (i) => PALETTE[i % PALETTE.length];

// Items 3/4 — coloration des trajets par TRANCHE de dépassement du SEUIL CONFIGURÉ du véhicule.
// Ce seuil n'est JAMAIS une limitation routière/légale (aucune source réglementaire utilisée).
// Indicateur distinct de vitesse absolue > 140 km/h (sécurité), indépendant du seuil véhicule.
export const ABS_SPEED_KMH = 140;
export const SPEED_SCALE = [
  { color: "#22C55E", label: "Sous le seuil" },
  { color: "#FACC15", label: "+1 à +20 km/h" },
  { color: "#F97316", label: "+20 à +30 km/h" },
  { color: "#EF4444", label: "+30 à +40 km/h" },
  { color: "#B91C1C", label: "+40 km/h et plus" },
];
export const ABS140_COLOR = "#7C3AED";
const speedTier = (sp, limit) => {
  const ex = (sp || 0) - (limit || 90);
  if (ex <= 0) return 0;
  if (ex <= 20) return 1;
  return Math.min(4, 2 + Math.floor((ex - 20 - 1e-9) / 10));
};
const speedColor = (sp, limit) => ((sp || 0) > ABS_SPEED_KMH ? ABS140_COLOR : SPEED_SCALE[speedTier(sp, limit)].color);
// Découpe la trace en segments CONSÉCUTIFS de même couleur (fusion des points de même tranche).
function speedSegments(points, limit) {
  const segs = [];
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i], b = points[i + 1];
    if (a.lat == null || b.lat == null) continue;
    const sp = Math.max(a.speed || 0, b.speed || 0);
    const color = speedColor(sp, limit);
    const rank = (sp > ABS_SPEED_KMH) ? 100 : speedTier(sp, limit);  // sévérité : excès dessinés au-dessus du vert
    const last = segs[segs.length - 1];
    if (last && last.color === color) last.positions.push([b.lat, b.lon]);
    else segs.push({ color, rank, positions: [[a.lat, a.lon], [b.lat, b.lon]] });
  }
  return segs;
}

// Multi-véhicules : 1 couleur DISTINCTE et STABLE par véhicule (dérivée du vehicle_id).
// Palette lisible sur satellite/OSM/sombre/clair, sans rouge alerte pur ni vert statut pur.
const VPALETTE = ["#2563EB", "#F5821F", "#14B8A6", "#A855F7", "#EC4899", "#EAB308", "#84CC16", "#06B6D4", "#6366F1", "#F97316", "#DB2777", "#0891B2"];
const hashStr = (s) => { let h = 0; const str = String(s || ""); for (let i = 0; i < str.length; i++) h = ((h << 5) - h + str.charCodeAt(i)) | 0; return Math.abs(h); };
// Assignation déterministe par ENSEMBLE de véhicules (indépendante de l'ordre de sélection / période / reload) :
// point de départ = hash(vehicle_id) puis 1er emplacement libre -> pas de collision tant que ≤ 12 véhicules.
export const buildColorMap = (trips) => {
  const vids = Array.from(new Set((trips || []).map((t) => t.vehicle_id))).sort();
  if (vids.length < 2) return null; // 1 seul véhicule -> comportement actuel (tripColor par trajet)
  const map = {}; const used = new Set();
  vids.forEach((vid) => {
    let idx = hashStr(vid) % VPALETTE.length; let tries = 0;
    while (used.has(idx) && tries < VPALETTE.length) { idx = (idx + 1) % VPALETTE.length; tries++; }
    used.add(idx); map[vid] = VPALETTE[idx];
  });
  return map;
};
export const legendItems = (trips) => {
  const cm = buildColorMap(trips);
  if (!cm) return [];
  const seen = new Map();
  (trips || []).forEach((t) => { if (t && cm[t.vehicle_id] && !seen.has(t.vehicle_id)) seen.set(t.vehicle_id, { id: t.vehicle_id, name: t.vehicle_name, plate: t.plate, color: cm[t.vehicle_id] }); });
  return Array.from(seen.values());
};

const fmtHM = (iso) => fmtTimeS(iso);

function InvalidateSize({ trigger }) {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 200);
    return () => clearTimeout(t);
  }, [map, trigger]);
  return null;
}

// Mode COMPARAISON — couleur par TRAJET (pas par véhicule) : 4 couleurs fortes distinctes,
// évitant vert(#10B981)/rouge(#EF4444) réservés aux drapeaux Départ/Arrivée.
export const COMPARE_PALETTE = ["#2563EB", "#F5821F", "#A855F7", "#06B6D4"];
export const compareColor = (pos) => COMPARE_PALETTE[pos % COMPARE_PALETTE.length];

function FitTrips({ trips, selectedIdx }) {
  const map = useMap();
  useEffect(() => {
    const src = selectedIdx != null && trips[selectedIdx] ? [trips[selectedIdx]] : trips;
    const pts = [];
    src.forEach((t) => (t.points || []).forEach((p) => pts.push([p.lat, p.lon])));
    if (pts.length === 1) map.setView(pts[0], 15);
    else if (pts.length > 1) map.fitBounds(pts, { padding: [50, 50], maxZoom: 16 });
  }, [trips, selectedIdx, map]);
  return null;
}

// Recentrage compare : fitBounds sur tous les trajets comparés ; recenter sur un trajet précis au clic.
function FitCompare({ trips, idxs, recenter }) {
  const map = useMap();
  useEffect(() => {
    const pts = [];
    (idxs || []).forEach((i) => (trips[i]?.points || []).forEach((p) => pts.push([p.lat, p.lon])));
    if (pts.length === 1) map.setView(pts[0], 15);
    else if (pts.length > 1) map.fitBounds(pts, { padding: [60, 60], maxZoom: 16 });
  }, [trips, idxs, map]);
  useEffect(() => {
    if (recenter == null || !trips[recenter]) return;
    const pts = (trips[recenter].points || []).map((p) => [p.lat, p.lon]);
    if (pts.length === 1) map.setView(pts[0], 15);
    else if (pts.length > 1) map.fitBounds(pts, { padding: [60, 60], maxZoom: 16 });
  }, [recenter, trips, map]);
  return null;
}

// A10 — Centre la carte sur la position mesurée au déclenchement (deep-link « hors horaires »).
function FlyToFocus({ point }) {
  const map = useMap();
  useEffect(() => { if (point && point.lat != null) map.setView([point.lat, point.lon], 16); }, [point, map]);
  return null;
}

function offsetLabel(s) {
  if (s == null) return "";
  const a = Math.abs(s);
  const txt = a < 60 ? `${a} s` : `${Math.round(a / 60)} min`;
  if (s === 0) return "à l'instant du déclenchement";
  return s < 0 ? `${txt} avant le déclenchement` : `${txt} après le déclenchement`;
}

export default function HistoryMap({ trips = [], selectedIdx = null, hoveredIdx = null, onSelect, visible = true, compareIdxs = [], hoverCompareIdx = null, recenterIdx = null, focusPoint = null }) {
  const [layer, setLayer] = useState("dark");
  const [activeEvents, setActiveEvents] = useState(() => Object.fromEntries(EVENT_ORDER.map((k) => [k, true])));
  const [hoverVeh, setHoverVeh] = useState(null);
  const [legendOpen, setLegendOpen] = useState(true);
  const [territory, setTerritory] = useState("nc");
  const [heatMode, setHeatMode] = useState(null);   // null | activity | dwell | stops
  const [showTrips, setShowTrips] = useState(true);
  const [colorMode, setColorMode] = useState("vehicle");   // items 3/4 : "vehicle" | "speed"

  const isCompare = (compareIdxs || []).length > 0;
  const colorMap = useMemo(() => buildColorMap(trips), [trips]);
  const legend = useMemo(() => legendItems(trips), [trips]);
  const colOf = (i) => (colorMap ? colorMap[(trips[i] || {}).vehicle_id] : tripColor(i));
  // Heatmap recalculée uniquement quand trajets/mode changent (respecte véhicules/conducteur/période déjà filtrés).
  const heatPoints = useMemo(() => (heatMode ? buildHeat(trips, heatMode) : []), [trips, heatMode]);
  const tripsVisible = showTrips || !heatMode;

  const selTrip = selectedIdx != null ? trips[selectedIdx] : null;

  // Types d'événements présents dans le trajet sélectionné (+ compteur)
  const presentTypes = useMemo(() => {
    const counts = {};
    (selTrip?.events || []).forEach((e) => { counts[e.type] = (counts[e.type] || 0) + 1; });
    return EVENT_ORDER.filter((k) => counts[k]).map((k) => ({ key: k, count: counts[k] }));
  }, [selTrip]);

  const toggleEvent = (k) => setActiveEvents((s) => ({ ...s, [k]: !s[k] }));

  return (
    <div style={{ height: "100%", width: "100%", position: "relative" }}>
      <div className="absolute top-3 right-3 z-[500] flex gap-1 glass rounded-md p-1" data-testid="history-map-layers">
        {HISTORY_LAYERS.map((k) => {
          const t = TILES[k];
          const disabled = t.region === "nc" && territory !== "nc";
          return (
            <button key={k} type="button" disabled={disabled} onClick={() => setLayer(k)} data-testid={`history-layer-${k}`}
              title={disabled ? "Fond régional NC — indisponible hors Nouvelle-Calédonie" : t.label}
              className={`px-2 py-1 rounded text-[11px] font-semibold transition-colors ${layer === k ? "bg-[#17A2B8] text-white" : "text-muted-foreground hover:text-foreground"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}>{t.label}</button>
          );
        })}
      </div>

      {/* Items 3/4 — Couleur des trajets : par véhicule (défaut) ou par tranche de dépassement du seuil configuré. */}
      {!isCompare && (
        <div className="absolute top-14 right-3 z-[500] flex gap-1 glass rounded-md p-1" data-testid="history-color-mode">
          <button type="button" onClick={() => setColorMode("vehicle")} data-testid="color-mode-vehicle"
            className={`px-2 py-1 rounded text-[11px] font-semibold transition-colors ${colorMode === "vehicle" ? "bg-[#17A2B8] text-white" : "text-muted-foreground hover:text-foreground"}`}>Véhicule</button>
          <button type="button" onClick={() => setColorMode("speed")} data-testid="color-mode-speed"
            className={`px-2 py-1 rounded text-[11px] font-semibold transition-colors ${colorMode === "speed" ? "bg-[#17A2B8] text-white" : "text-muted-foreground hover:text-foreground"}`}>Vitesse / seuil</button>
        </div>
      )}
      {!isCompare && (
      <div className="absolute top-3 left-3 z-[500] glass rounded-lg p-1.5 w-[184px]" data-testid="history-heat-panel">
        <div className="flex items-center gap-1.5 px-1 mb-1">
          <Flame className="w-3.5 h-3.5 text-[#F5821F]" />
          <span className="text-[11px] font-bold">Carte thermique</span>
        </div>
        <div className="grid grid-cols-2 gap-1">
          <button type="button" onClick={() => setHeatMode(null)} data-testid="heat-mode-off"
            className={`px-2 py-1 rounded text-[10px] font-semibold transition-colors ${!heatMode ? "bg-[#17A2B8] text-white" : "text-muted-foreground hover:text-foreground border border-border"}`}>Aucune</button>
          {Object.entries(HEAT_MODES).map(([k, lbl]) => (
            <button key={k} type="button" onClick={() => setHeatMode(k)} data-testid={`heat-mode-${k}`}
              className={`px-2 py-1 rounded text-[10px] font-semibold transition-colors ${heatMode === k ? "bg-[#F5821F] text-white" : "text-muted-foreground hover:text-foreground border border-border"}`}>{lbl}</button>
          ))}
        </div>
        {heatMode && (
          <div className="mt-1.5 pt-1.5 border-t border-border/60">
            <button type="button" onClick={() => setShowTrips((s) => !s)} data-testid="heat-toggle-trips"
              className="w-full flex items-center gap-2 px-1 py-1 text-[10px] font-semibold hover:text-[#17A2B8] transition-colors">
              <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${showTrips ? "bg-[#17A2B8] border-[#17A2B8]" : "border-border"}`}>{showTrips && <span className="w-1.5 h-1.5 bg-white rounded-sm" />}</span>
              Afficher aussi les trajets
            </button>
            <div className="mt-1 px-1" data-testid="heat-legend">
              <div className="h-1.5 rounded-full" style={{ background: "linear-gradient(90deg,#2563EB,#06B6D4,#84CC16,#F59E0B,#EF4444)" }} />
              <div className="flex justify-between text-[9px] text-muted-foreground mt-0.5"><span>Faible</span><span className="font-semibold">{HEAT_MODES[heatMode]}</span><span>Forte</span></div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Légende COMPARAISON — couleur/véhicule/date/heure départ par trajet comparé. */}
      {isCompare && (
        <div className="absolute top-3 left-3 z-[500] glass rounded-lg p-1.5 max-w-[240px]" data-testid="history-compare-legend">
          <p className="label-mono mb-1 px-1">Comparaison ({compareIdxs.length})</p>
          <div className="space-y-0.5">
            {compareIdxs.map((ti, pos) => {
              const t = trips[ti]; if (!t) return null;
              return (
                <div key={ti} data-testid={`compare-legend-${ti}`}
                  className={`flex items-center gap-2 px-1.5 py-1 rounded text-[11px] transition-opacity ${hoverCompareIdx != null && hoverCompareIdx !== ti ? "opacity-40" : ""}`}>
                  <span className="w-3 h-3 rounded-full shrink-0" style={{ background: compareColor(pos) }} />
                  <span className="font-semibold truncate">{t.vehicle_name}</span>
                  <span className="text-muted-foreground shrink-0 data text-[10px]">{fmtTimeS(t.start)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {/* Calques d'événements — visibles quand un trajet est sélectionné et a des événements */}
      {selTrip && presentTypes.length > 0 && (
        <div className="absolute bottom-4 right-3 z-[500] glass rounded-lg p-2 w-[190px]" data-testid="history-event-layers">
          <p className="label-mono mb-1.5 px-1">Calques d'événements</p>
          <div className="space-y-1">
            {presentTypes.map(({ key, count }) => {
              const cfg = EVENT_TYPES[key];
              const Ic = cfg.Icon;
              const on = activeEvents[key];
              return (
                <button key={key} type="button" onClick={() => toggleEvent(key)} data-testid={`event-layer-${key}`}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs font-semibold transition-all border ${on ? "border-white/10 bg-white/5" : "border-transparent opacity-45"}`}>
                  <span className="w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ background: on ? cfg.color : "transparent", border: `1.5px solid ${cfg.color}` }}>
                    <Ic className="w-3 h-3" style={{ color: on ? "#fff" : cfg.color }} />
                  </span>
                  <span className="flex-1 text-left truncate">{cfg.label}</span>
                  <span className="data text-[10px] text-muted-foreground">{count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Légende — 1 pastille par véhicule (couleur du tracé). Repliable, compacte sur mobile. */}
      {!isCompare && colorMode === "vehicle" && legend.length >= 2 && (
        <div className="absolute bottom-4 left-3 z-[500] glass rounded-lg overflow-hidden max-w-[220px]" data-testid="history-legend">
          <button type="button" onClick={() => setLegendOpen((o) => !o)} data-testid="history-legend-toggle"
            className="w-full flex items-center gap-2 px-2.5 py-1.5 text-[11px] font-bold hover:text-[#17A2B8] transition-colors">
            <span className="w-2 h-2 rounded-full bg-[#17A2B8]" />
            Véhicules ({legend.length})
            <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform ${legendOpen ? "" : "-rotate-90"}`} />
          </button>
          {legendOpen && (
            <div className="px-1.5 pb-1.5 space-y-0.5 max-h-[40vh] overflow-y-auto">
              {legend.map((it) => (
                <div key={it.id} data-testid={`history-legend-item-${it.id}`}
                  onMouseEnter={() => setHoverVeh(it.id)} onMouseLeave={() => setHoverVeh(null)}
                  className={`flex items-center gap-2 px-2 py-1 rounded text-[11px] font-semibold cursor-default transition-opacity ${hoverVeh && hoverVeh !== it.id ? "opacity-40" : ""}`}>
                  <span className="w-3 h-3 rounded-full shrink-0" style={{ background: it.color }} />
                  <span className="truncate">{it.name}</span>
                  {it.plate && <span className="text-muted-foreground font-normal truncate">· {it.plate}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Items 3/4 — Légende « Seuil configuré du véhicule » (tranches de dépassement). Jamais une limite légale. */}
      {!isCompare && colorMode === "speed" && (
        <div className="absolute bottom-4 left-3 z-[500] glass rounded-lg p-2 max-w-[240px]" data-testid="history-speed-legend">
          <p className="text-[11px] font-bold mb-1">
            Seuil configuré du véhicule{selTrip?.speed_limit_kmh ? ` : ${selTrip.speed_limit_kmh} km/h` : ""}
          </p>
          <div className="space-y-0.5">
            {SPEED_SCALE.map((s, i) => (
              <div key={i} className="flex items-center gap-2 text-[10px]" data-testid={`speed-legend-tier-${i}`}>
                <span className="w-4 h-1.5 rounded-full shrink-0" style={{ background: s.color }} />
                <span className="text-muted-foreground">{s.label}</span>
              </div>
            ))}
            <div className="flex items-center gap-2 text-[10px]" data-testid="speed-legend-abs140">
              <span className="w-4 h-1.5 rounded-full shrink-0" style={{ background: ABS140_COLOR }} />
              <span className="text-muted-foreground">Vitesse absolue &gt; 140 km/h</span>
            </div>
          </div>
          <p className="text-[9px] text-muted-foreground mt-1 italic">Seuil de configuration, pas une limitation routière.</p>
        </div>
      )}

      <MapContainer center={[-22.2758, 166.458]} zoom={12} maxZoom={MAP_MAX_ZOOM} zoomControl={false} style={{ height: "100%", width: "100%", background: "#09090b" }}>
        <InvalidateSize trigger={visible} />
        {!isCompare && !focusPoint && <FitTrips trips={trips} selectedIdx={selectedIdx} />}
        {focusPoint && <FlyToFocus point={focusPoint} />}
        {isCompare && <FitCompare trips={trips} idxs={compareIdxs} recenter={recenterIdx} />}
        <BaseTileLayer layer={layer} />
        <TileManager layer={layer} setLayer={setLayer} onTerritory={setTerritory} />
        <ZoomInfo layer={layer} position="bottomright" />
        {!isCompare && heatMode && <HeatLayer points={heatPoints} />}

        {/* MODE COMPARAISON : uniquement les trajets sélectionnés, 1 couleur par trajet + Départ/Arrivée. */}
        {isCompare && compareIdxs.map((ti, pos) => {
          const t = trips[ti];
          if (!t || !t.points || t.points.length < 2) return null;
          const color = compareColor(pos);
          const dim = hoverCompareIdx != null && hoverCompareIdx !== ti;
          const s = t.points[0], e = t.points[t.points.length - 1];
          return (
            <Fragment key={`cmp-${ti}`}>
              <Polyline positions={t.points.map((p) => [p.lat, p.lon])}
                pathOptions={{ color, weight: hoverCompareIdx === ti ? 7 : 4, opacity: dim ? 0.25 : 0.95 }}
                eventHandlers={{ click: () => onSelect && onSelect(ti) }} />
              <Marker position={[s.lat, s.lon]} icon={tripFlagIcon("start")}>
                <Popup><b>Départ</b> · {t.vehicle_name}<br />{fmtHM(t.start)}</Popup>
              </Marker>
              <Marker position={[e.lat, e.lon]} icon={tripFlagIcon("end")}>
                <Popup><b>Arrivée</b> · {t.vehicle_name}<br />{fmtHM(t.end)}</Popup>
              </Marker>
            </Fragment>
          );
        })}

        {!isCompare && tripsVisible && trips.map((t, i) => {
          if (!t.points || t.points.length < 2) return null;
          // Au clic : n'afficher QUE le trajet sélectionné
          if (selectedIdx !== null && selectedIdx !== i) return null;
          const emph = selectedIdx !== null ? selectedIdx : hoveredIdx;
          const vehEmph = hoverVeh != null && t.vehicle_id === hoverVeh;
          const active = (emph === null || emph === i) && (hoverVeh == null || vehEmph);
          const weight = (emph === i || vehEmph) ? 6 : 3;
          const opacity = active ? 0.95 : 0.25;
          if (colorMode === "speed") {
            // Segments colorés par tranche de dépassement du seuil configuré du véhicule (jamais une limite légale).
            // Tri par sévérité croissante : les excès sont dessinés APRÈS (au-dessus) du vert, jamais masqués.
            return speedSegments(t.points, t.speed_limit_kmh)
              .sort((x, y) => x.rank - y.rank)
              .map((sg, si) => (
              <Polyline key={`tp-${i}-${si}`} positions={sg.positions}
                pathOptions={{ color: sg.color, weight, opacity }}
                eventHandlers={{ click: () => onSelect && onSelect(i) }} />
            ));
          }
          const color = colOf(i);
          const pos = t.points.map((p) => [p.lat, p.lon]);
          return (
            <Polyline key={`tp-${i}`} positions={pos}
              pathOptions={{ color, weight, opacity }}
              eventHandlers={{ click: () => onSelect && onSelect(i) }} />
          );
        })}

        {/* Drapeaux + événements uniquement pour le trajet sélectionné */}
        {!isCompare && tripsVisible && selTrip && selTrip.points?.length > 0 && (() => {
          const s = selTrip.points[0], e = selTrip.points[selTrip.points.length - 1];
          return (
            <>
              {noSignalSegments(selTrip.points).map((sg, gi) => (
                <Polyline key={`ns-${gi}`} positions={sg.positions} pathOptions={{ color: "#F59E0B", weight: 6, opacity: 0.95, dashArray: "6 9" }}>
                  <Tooltip sticky>Tronçon sans réseau mobile · {sg.gapMin} min<br />(données mises en mémoire par le boîtier)</Tooltip>
                </Polyline>
              ))}
              <Marker position={[s.lat, s.lon]} icon={tripFlagIcon("start")}>
                <Popup><b>Départ</b> · {selTrip.vehicle_name}<br />{fmtHM(selTrip.start)}</Popup>
              </Marker>
              <Marker position={[e.lat, e.lon]} icon={tripFlagIcon("end")}>
                <Popup><b>Arrivée</b> · {selTrip.vehicle_name}<br />{fmtHM(selTrip.end)}</Popup>
              </Marker>
              {/* Flèches de direction (orientées selon le cap) — cliquables avec données CANbus du point */}
              {(() => {
                const pts = selTrip.points;
                const step = pts.length > 120 ? Math.ceil(pts.length / 120) : 1;
                const col = colOf(selectedIdx);
                return pts.map((p, k) => {
                  if (k === 0 || k === pts.length - 1) return null;
                  if (k % step !== 0) return null;
                  const moving = (p.speed || 0) > 3;
                  const icon = moving ? tripArrowIcon(p.angle || 0, col) : tripDotIcon("#94a3b8");
                  const canKeys = p.can ? CAN_ORDER.filter((ck) => p.can[ck] != null) : [];
                  const hasCan = canKeys.length > 0;
                  return (
                    <Marker key={`pt-${k}`} position={[p.lat, p.lon]} icon={icon}>
                      <Popup>
                        <div style={{ minWidth: 150 }} data-testid={`trip-point-${k}`}>
                          <b>{fmtHM(p.timestamp)}</b> · {Math.round(p.speed || 0)} km/h · cap {p.angle ?? 0}°
                          {hasCan ? (
                            <div style={{ marginTop: 4, borderTop: "1px solid rgba(0,0,0,.1)", paddingTop: 4 }}>
                              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".05em", opacity: .6, marginBottom: 2 }}>Données CANbus</div>
                              {canKeys.map((ck) => (
                                <div key={ck} style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                                  <span style={{ opacity: .7 }}>{(CAN_FIELDS[ck] || {}).label || ck}</span>
                                  <b>{p.can[ck]}{(CAN_FIELDS[ck] || {}).unit ? ` ${CAN_FIELDS[ck].unit}` : ""}</b>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={{ marginTop: 4, fontSize: 11, opacity: .6 }}>Aucune donnée CANbus sur ce point</div>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  );
                });
              })()}
              {(selTrip.events || []).map((ev, k) => {
                if (!activeEvents[ev.type]) return null;
                const cfg = EVENT_TYPES[ev.type];
                if (!cfg) return null;
                return (
                  <CircleMarker key={`ev-${k}`} center={[ev.lat, ev.lon]} radius={5}
                    pathOptions={{ color: "#fff", fillColor: cfg.color, fillOpacity: 1, weight: 1.5 }}>
                    <Popup>
                      <b>{cfg.label}</b> · {fmtHM(ev.timestamp)}
                      {ev.duration_min ? <><br />Durée : {ev.duration_min} min</> : null}
                      {ev.value != null && !ev.duration_min ? <><br />Valeur : {ev.value}</> : null}
                    </Popup>
                  </CircleMarker>
                );
              })}
            </>
          );
        })()}
        {/* A10 — Position mesurée au déclenchement (deep-link « hors horaires »). Mesure réelle, jamais interpolée. */}
        {focusPoint && focusPoint.lat != null && (
          <CircleMarker center={[focusPoint.lat, focusPoint.lon]} radius={10}
            pathOptions={{ color: "#fff", fillColor: "#F5821F", fillOpacity: 1, weight: 3 }}>
            <Popup>
              <div data-testid="focus-trigger-popup" style={{ minWidth: 170 }}>
                <b>Position au déclenchement</b><br />
                Mesure GPS à {fmtHM(focusPoint.timestamp)}
                {focusPoint.offset_seconds != null && (
                  <><br /><span style={{ fontSize: 11, opacity: 0.7 }}>{offsetLabel(focusPoint.offset_seconds)}</span></>
                )}
              </div>
            </Popup>
            <Tooltip permanent direction="top" offset={[0, -8]}>Déclenchement</Tooltip>
          </CircleMarker>
        )}
      </MapContainer>
    </div>
  );
}
